import React, { useState } from "react";
import axios from "axios";
import { Mail, MapPin, Send } from "lucide-react";
import Reveal from "./Reveal";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { useToast } from "../hooks/use-toast";
import { CONTACT_INFO } from "../mock/mock";
import { trackEvent } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const { toast } = useToast();

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email.includes("@") || !form.message) {
      toast({ title: "Almost there", description: "Please fill in all fields with a valid email." });
      return;
    }
    // Capture the email so a lead is never lost.
    axios.post(`${API}/subscribe`, { email: form.email.trim(), source: "contact" }, { timeout: 15000 }).catch(() => {});
    trackEvent("sign_up", { method: "contact" });
    trackEvent("contact_submit", {});
    toast({ title: "Message sent \ud83d\ude80", description: "Your message has been sent, thank you!" });
    setForm({ name: "", email: "", message: "" });
  };

  return (
    <section id="contact" className="bg-slate-900 py-24 sm:py-32 relative overflow-hidden">
      <div className="absolute bottom-0 left-1/3 w-[500px] h-[500px] bg-teal-500/10 rounded-full blur-[130px]" />
      <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14">
        <Reveal>
          <p className="text-cyan-400 font-bold tracking-widest uppercase text-sm mb-3">Contact</p>
          <h2 className="font-display font-800 text-4xl sm:text-5xl lg:text-6xl text-white leading-tight">
            Let's build something <span className="text-gradient">magical</span>.
          </h2>
          <p className="text-white/60 text-lg mt-6 max-w-md">{CONTACT_INFO.note}</p>

          <div className="mt-10 space-y-5">
            <a href={`mailto:${CONTACT_INFO.email}`} className="flex items-center gap-4 group">
              <span className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center group-hover:bg-teal-600 transition-colors">
                <Mail className="w-5 h-5 text-cyan-400 group-hover:text-white" />
              </span>
              <div>
                <div className="text-white/40 text-sm">Email us</div>
                <div className="text-white font-semibold">{CONTACT_INFO.emailDisplay || CONTACT_INFO.email}</div>
              </div>
            </a>
            <div className="flex items-center gap-4">
              <span className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-cyan-400" />
              </span>
              <div>
                <div className="text-white/40 text-sm">Based in</div>
                <div className="text-white font-semibold">{CONTACT_INFO.location}</div>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={120}>
          <form
            onSubmit={submit}
            className="bg-white/5 border border-white/10 rounded-3xl p-8 backdrop-blur-sm"
          >
            <div className="space-y-5">
              <div>
                <label className="text-white/70 text-sm font-medium mb-2 block">Name</label>
                <Input
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Your name"
                  className="h-12 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-cyan-500"
                />
              </div>
              <div>
                <label className="text-white/70 text-sm font-medium mb-2 block">Email</label>
                <Input
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@email.com"
                  className="h-12 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-cyan-500"
                />
              </div>
              <div>
                <label className="text-white/70 text-sm font-medium mb-2 block">Message</label>
                <Textarea
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Tell us about your project or say hi..."
                  rows={4}
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-cyan-500 resize-none"
                />
              </div>
              <Button
                type="submit"
                className="w-full h-12 rounded-full bg-teal-600 hover:bg-teal-700 text-white font-semibold"
              >
                Send message <Send className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
}